import React from 'react';
import { FCRowProps } from '../../types/bulkR2fTypes';
import { PendingThresholdChange } from '../../types/r2fTypes';
import { EditableThresholdCell } from './EditableThresholdCell';
import { getDisplayName } from '../../utils/displayUtils';

/**
 * Component for displaying a collapsible FC (warehouse) row with production capabilities
 * Memoized to prevent unnecessary re-renders when parent state changes
 */
const FCRowComponent: React.FC<FCRowProps & {
    isExpanded: boolean;
    shouldShowProductionCapability?: (pcName: string) => boolean;
}> = ({
    warehouse,
    tierName,
    onToggleExpand,
    onValueChange,
    userCanEdit,
    pendingChanges,
    isExpanded,
    shouldShowProductionCapability,
    invalidRows
}) => {
    const handleToggle = () => {
        onToggleExpand(warehouse.warehouseId);
    };

    // Get filtered production capabilities count
    const filteredPcCount = shouldShowProductionCapability
        ? warehouse.productionCapabilities.filter(pc => shouldShowProductionCapability(pc.name)).length
        : warehouse.productionCapabilities.length;

    return (
        <div className="fc-row">
            <div className="fc-header" onClick={handleToggle}>
                <div className="fc-info">
                    <span className="expand-icon">
                        {isExpanded ? '▼' : '▶'}
                    </span>
                    <span className="warehouse-id">{warehouse.warehouseId}</span>
                    <span className="pc-count">
                        {filteredPcCount} production capabilit{filteredPcCount !== 1 ? 'ies' : 'y'}
                    </span>
                </div>
            </div>
            
            {isExpanded && (
                <div className="fc-content">
                    <div className="pc-rows">
                        {warehouse.productionCapabilities
                            .filter((pc) => !shouldShowProductionCapability || shouldShowProductionCapability(pc.name))
                            .map((pc) => {
                                const changeKey = `${warehouse.warehouseId}-${pc.name}-${tierName}`;
                                const pendingChange = pendingChanges[changeKey];
                                const hasValidationError = invalidRows?.has(changeKey) || false;
                                
                                return (
                                    <div key={pc.name} className={`pc-row ${hasValidationError ? 'validation-error' : ''}`}>
                                        <div className="pc-name-col">
                                            <span className="pc-name">{getDisplayName(pc.name)}</span>
                                            {pendingChange && (
                                                <span className="pending-indicator">●</span>
                                            )}
                                        </div>
                                        
                                        <div className="threshold-col">
                                            <EditableThresholdCell
                                                value={pendingChange?.lowThreshold ?? pc.lowThreshold}
                                                onChange={(value: number) => onValueChange({
                                                    warehouseId: warehouse.warehouseId,
                                                    productionCapability: pc.name,
                                                    field: 'lowThreshold',
                                                    value,
                                                    tierName // Pass actual tier name instead of hardcoded "Default"
                                                })}
                                                disabled={!userCanEdit}
                                                hasPendingChange={!!pendingChange}
                                                placeholder="Low"
                                            />
                                        </div>
                                        
                                        <div className="threshold-col">
                                            <EditableThresholdCell
                                                value={pendingChange?.highThreshold ?? pc.highThreshold}
                                                onChange={(value: number) => onValueChange({
                                                    warehouseId: warehouse.warehouseId,
                                                    productionCapability: pc.name,
                                                    field: 'highThreshold',
                                                    value,
                                                    tierName // Pass actual tier name instead of hardcoded "Default"
                                                })}
                                                disabled={!userCanEdit}
                                                hasPendingChange={!!pendingChange}
                                                placeholder="High"
                                            />
                                        </div>
                                        
                                        <div className="meta-col">
                                            {pc.publishDateEpochMillis && (
                                                <div className="last-updated">
                                                    <div className="date">
                                                        {new Date(pc.publishDateEpochMillis).toLocaleDateString()}
                                                    </div>
                                                    <div className="user">
                                                        {pc.userAlias || 'Unknown'}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Memoized FCRow component with custom comparison function to prevent unnecessary re-renders
 */
export const FCRow = React.memo(FCRowComponent, (prevProps, nextProps) => {
    // Compare all relevant props to determine if re-render is needed
    return (
        prevProps.warehouse.warehouseId === nextProps.warehouse.warehouseId &&
        prevProps.tierName === nextProps.tierName &&
        prevProps.userCanEdit === nextProps.userCanEdit &&
        prevProps.isExpanded === nextProps.isExpanded &&
        // Deep compare production capabilities
        JSON.stringify(prevProps.warehouse.productionCapabilities) === JSON.stringify(nextProps.warehouse.productionCapabilities) &&
        // Compare pending changes for this warehouse
        JSON.stringify(prevProps.pendingChanges) === JSON.stringify(nextProps.pendingChanges) &&
        // Compare filter function reference (if it's the same function, no need to re-render)
        prevProps.shouldShowProductionCapability === nextProps.shouldShowProductionCapability &&
        // Compare invalid rows set
        ((prevProps.invalidRows === nextProps.invalidRows) ||
         (!!prevProps.invalidRows && !!nextProps.invalidRows &&
          Array.from(prevProps.invalidRows).join(',') === Array.from(nextProps.invalidRows).join(',')) ||
         (!prevProps.invalidRows && !nextProps.invalidRows))
    );
});